"use client"

import { useState, useEffect } from "react"
import { FaEdit, FaTrash } from "react-icons/fa"
import { IoMdAdd, IoMdSearch } from "react-icons/io"
import Modal from "../components/BannerModal.js"
import { fetchBanners, togglePublished, addBanner, deleteBanner, updateBanner } from "../comman/Banner.js"
import Sidebar from "../components/Sidebar.js"

const BannerTable = () => {
  const [data, setData] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bannerToEdit, setBannerToEdit] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      const banners = await fetchBanners()
      setData(banners)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching banners:", error)
      setError("Failed to load banners. Please try again.")
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAddBanner = async (newBannerData) => {
    try {
      await addBanner(newBannerData)
      setIsModalOpen(false)
      fetchData()
    } catch (error) {
      console.error("Error adding banner:", error)
    }
  }

  const handleUpdateBanner = async (updatedBannerData) => {
    try {
      await updateBanner(updatedBannerData)
      setIsModalOpen(false)
      fetchData()
    } catch (error) {
      console.error("Error updating banner:", error)
    }
  }

  const handleToggle = async (id) => {
    try {
      const updatedBanner = await togglePublished(id)
      if (updatedBanner && updatedBanner.banner) {
        setData((prevData) =>
          prevData.map((item) =>
            item._id === id ? { ...item, requestStatus: updatedBanner.banner.requestStatus } : item,
          ),
        )
      }
    } catch (error) {
      console.error("Error toggling published status:", error)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        await deleteBanner(id)
        setData((prevData) => prevData.filter((item) => item._id !== id))
      } catch (error) {
        console.error("Error deleting banner:", error)
      }
    }
  }

  const handleEdit = (banner) => {
    setBannerToEdit(banner)
    setIsModalOpen(true)
  }

  const filteredData = data.filter((row) => (row.bannerType || "").toLowerCase().includes(searchQuery.toLowerCase()))

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-[#12143F]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 md:ml-64">
        <h1 className="text-2xl md:text-3xl font-bold text-[#12143F] mb-6">Banner Management</h1>
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0 md:space-x-4">
            <div className="relative w-full md:w-64">
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm text-gray-900 border border-gray-300 rounded-lg focus:ring-[#12143F] focus:border-[#12143F]"
                placeholder="Search banner type"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <IoMdSearch className="w-5 h-5 text-gray-500" />
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90 transition duration-300"
            >
              <IoMdAdd size={18} className="mr-2" />
              Add Banner
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-white uppercase bg-[#12143F]">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Sr. No
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Image
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Banner Type
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Promotion Start
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Promotion End
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Published For
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Published
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <tr key={row._id} className="border-b hover:bg-gray-50">
                    <td className="px-6 py-4">{index + 1}</td>
                    <td className="px-6 py-4">
                      {row.imageUrl ? (
                        <img
                          src={row.imageUrl || "/placeholder.svg"}
                          alt={row.bannerType}
                          className="w-20 h-12 object-cover border border-gray-200 rounded"
                        />
                      ) : (
                        <span className="text-gray-400">No Image</span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{row.bannerType}</td>
                    <td className="px-6 py-4">{new Date(row.fromDate).toLocaleDateString()}</td>
                    <td className="px-6 py-4">{new Date(row.toDate).toLocaleDateString()}</td>
                    <td className="px-6 py-4">{row.publishOption}</td>
                    <td className="px-6 py-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={row.requestStatus}
                          onChange={() => handleToggle(row._id)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleEdit(row)}
                        className="text-[#12143F] hover:text-opacity-80 transition-colors duration-200 mr-3"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(row._id)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200"
                      >
                        <FaTrash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setBannerToEdit(null)
        }}
        onAddBanner={handleAddBanner}
        bannerToEdit={bannerToEdit}
        onUpdateBanner={handleUpdateBanner}
      />
    </div>
  )
}

export default BannerTable

